import { useEffect, useRef, useState } from 'react';

import { Badge, Box, Stack, Text, Tooltip, useColorModeValue } from '@ecoinvent/ui';

import ProcessTypeIcon, { getProcessTypeBadgeStyle } from 'components/UI/ProcessTypeIcon';
import TooltipBody from 'components/UI/TooltipBody';
import TooltipHeader from 'components/UI/TooltipHeader';
import { SupplyChainNode } from 'hooks/data/datasets/V4/supplyChain/types';
import { supplierTypeToDisplayName, supplierTypeToTooltipLabel } from 'pages/Flows/FlowsView/utils/constants';

import {
  COLORS,
  FUNCTIONAL_ACCENT_DARK,
  FUNCTIONAL_ACCENT_LIGHT,
  SUPPLY_CARD_ACCENT_WIDTH,
  SUPPLY_CARD_BORDER_WIDTH,
  SUPPLY_CARD_ICON_GAP,
  SUPPLY_CARD_ICON_SIZE,
  SUPPLY_CARD_PADDING_LEFT_HIGHLIGHTED,
  SUPPLY_CARD_PADDING_X,
  SUPPLY_CARD_PADDING_Y,
  SUPPLY_CARD_TEXT_INDENT,
  SUPPLY_NODE_WIDTH,
} from './constants';
import ImpactAssessmentButtons from './ImpactAssessmentButtons';
import ImpactScore, { ImpactScoreState } from './ImpactScore';
import type { NodeVariant } from './SupplyChainNode';
import { stopEvents } from './utils';
import ViewDatasetButton from './ViewDatasetButton';

// Chakra's focus-visible reset also clears box-shadow, so elevation and the keyboard ring are re-asserted here.
const RESTORE_SHADOW_SX = {
  boxShadow: 'var(--chakra-shadows-sm) !important',
  '&:focus-visible': { boxShadow: 'var(--chakra-shadows-outline) !important' },
};

type CardAccentArgs = {
  nodeVariant: NodeVariant;
  isRoot?: boolean;
  isActive?: boolean;
  isFunctional?: boolean;
  mode?: 'flows' | 'impact_assessment';
};

// Exported so the flow-direction badge outside the card cannot disagree with the card's own stripe,
// including when selection takes the card over.
export const useCardAccent = ({
  nodeVariant,
  isRoot,
  isActive,
  isFunctional,
  mode,
}: CardAccentArgs): { accentColor: string; flowColor: string; isHighlighted: boolean; isFunctionalAccent: boolean } => {
  const consumerBorderColor = useColorModeValue(COLORS.purpleLight, COLORS.purpleDark);
  const functionalAccentColor = useColorModeValue(FUNCTIONAL_ACCENT_LIGHT, FUNCTIONAL_ACCENT_DARK);

  const accentColorMap: Record<NodeVariant, string> = {
    root: '',
    input: COLORS.inputBorder,
    consumer: consumerBorderColor,
  };
  // Root and selected share one treatment: blue border on all four sides, so the left accent stripe gives way.
  const isHighlighted = Boolean(isRoot || isActive);
  // Layered on top of the variant rather than replacing it: the functional flow can be an input or an output.
  const isFunctionalAccent = Boolean(isFunctional) && mode === 'impact_assessment';
  // Flow colour is independent of selection; only the card's own accent flips to blue when highlighted.
  const flowColor = isFunctionalAccent ? functionalAccentColor : accentColorMap[nodeVariant];
  const accentColor = isHighlighted ? COLORS.rootOutline : flowColor;

  return { accentColor, flowColor, isHighlighted, isFunctionalAccent };
};

type Props = {
  node: SupplyChainNode;
  nodeVariant?: NodeVariant;
  isRoot?: boolean;
  isActive?: boolean;
  // Comes from the layout's edge-derived set, never from `node.is_functional`, which is frozen at first sight.
  isFunctional?: boolean;
  mode?: 'flows' | 'impact_assessment';
  // Required, and never re-derived from `mode` here: the layout's value also sets the row pitch.
  nodeHeight: number;
  // Resolved once by the bulk fetch in the canvas and stamped onto the payload, so the score row and Copy
  // Score always agree; the card no longer fetches anything itself.
  scoreStatus: ImpactScoreState['status'];
  score?: number;
  scoreUnit?: string;
  onSelect: () => void;
  onViewDataset?: () => void;
  onCopyScore?: () => Promise<void>;
};

const SupplyChainCard = ({
  node,
  nodeVariant = 'input',
  isRoot,
  isActive,
  isFunctional,
  mode,
  nodeHeight,
  scoreStatus,
  score,
  scoreUnit,
  onSelect,
  onViewDataset,
  onCopyScore,
}: Props) => {
  const titleRef = useRef<HTMLParagraphElement>(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);
  // Only the `ready` variant carries a value; every other status renders a fixed-height placeholder row.
  const scoreState: ImpactScoreState =
    scoreStatus === 'ready' ? { status: 'ready', score: score ?? 0, unit: scoreUnit ?? '' } : { status: scoreStatus };

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    const check = () => setIsTitleTruncated(el.scrollWidth > el.offsetWidth);
    check();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [node.name]);

  const bg = useColorModeValue('white', COLORS.darkSurface);
  const subtextColor = useColorModeValue(COLORS.grayDark, COLORS.grayDim);
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const rootOutlineColor = COLORS.rootOutline;
  const {
    accentColor: cardAccentColor,
    isHighlighted,
    isFunctionalAccent,
  } = useCardAccent({
    nodeVariant,
    isRoot,
    isActive,
    isFunctional,
    mode,
  });

  const badgeStyle = getProcessTypeBadgeStyle(node.type);
  const typeDescription = supplierTypeToTooltipLabel[node.type];

  const metaParts = [node.geography, node.time_period, ...(node.sectors ?? [])].filter(Boolean);

  // currentTarget guard: without it Enter/Space on the nested buttons is preventDefault'ed and they become mouse-only.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    onSelect();
  };

  return (
    <Box
      bg={bg}
      border={`${SUPPLY_CARD_BORDER_WIDTH}px solid`}
      borderColor={isHighlighted ? rootOutlineColor : borderColor}
      borderLeft={isHighlighted ? undefined : `${SUPPLY_CARD_ACCENT_WIDTH}px solid ${cardAccentColor}`}
      borderRadius="md"
      pt={`${SUPPLY_CARD_PADDING_Y}px`}
      pb={`${SUPPLY_CARD_PADDING_Y}px`}
      // pl/pr rather than px: px emits padding-inline-*, so a padding-left override would win only by declaration order.
      pl={`${isHighlighted ? SUPPLY_CARD_PADDING_LEFT_HIGHLIGHTED : SUPPLY_CARD_PADDING_X}px`}
      pr={`${SUPPLY_CARD_PADDING_X}px`}
      w={`${SUPPLY_NODE_WIDTH}px`}
      h={`${nodeHeight}px`}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      overflow="hidden"
      boxShadow="sm"
      transition="box-shadow 0.15s"
      className="nopan nodrag"
      cursor="pointer"
      // !important: Chakra's focus-visible reset clears outline on pointer focus, hiding the selection ring.
      sx={
        isHighlighted
          ? { ...RESTORE_SHADOW_SX, outline: `5px solid ${rootOutlineColor} !important`, outlineOffset: '2px' }
          : undefined
      }
      _focusVisible={{ boxShadow: 'outline' }}
      role="group"
      aria-label={isFunctionalAccent ? `${node.name}, functional flow` : node.name}
      aria-current={isActive ? 'true' : undefined}
      tabIndex={0}
      onClick={(e) => {
        stopEvents(e);
        onSelect();
      }}
      onKeyDown={handleKeyDown}
      onPointerDown={stopEvents}
      onMouseDown={stopEvents}
    >
      <Stack spacing={1}>
        <Stack direction="row" align="flex-start" spacing={`${SUPPLY_CARD_ICON_GAP}px`}>
          <Tooltip
            hasArrow
            placement="top-start"
            p={2}
            label={
              <Stack spacing={0}>
                <TooltipHeader>{supplierTypeToDisplayName[node.type] ?? node.type}</TooltipHeader>
                {typeDescription && <TooltipBody>{typeDescription}</TooltipBody>}
              </Stack>
            }
          >
            <Badge
              bg={badgeStyle.bg}
              color={badgeStyle.iconColor}
              _dark={{ bg: badgeStyle.bg, color: badgeStyle.iconColor }}
              borderRadius="base"
              p={1}
              w={`${SUPPLY_CARD_ICON_SIZE}px`}
              h={`${SUPPLY_CARD_ICON_SIZE}px`}
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <ProcessTypeIcon
                icon={badgeStyle.icon}
                subIcon={badgeStyle.subIcon}
                boxSize="18px"
                subBoxSize="8px"
                subOffset="-2px"
              />
            </Badge>
          </Tooltip>
          <Tooltip
            hasArrow
            isDisabled={!isTitleTruncated}
            label={<TooltipHeader>{node.name}</TooltipHeader>}
            openDelay={500}
            placement="top"
            p={3}
            maxW="fit-content"
          >
            <Text ref={titleRef} fontWeight="bold" fontSize="md" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
              {node.name}
            </Text>
          </Tooltip>
        </Stack>
        {metaParts.length > 0 && (
          <Text fontSize="xs" color={subtextColor} pl={`${SUPPLY_CARD_TEXT_INDENT}px`} noOfLines={2}>
            {metaParts.join(' · ')}
          </Text>
        )}
        {mode === 'impact_assessment' && <ImpactScore state={scoreState} isLive={isRoot || isActive} />}
        {mode === 'impact_assessment' && onViewDataset && onCopyScore ? (
          <ImpactAssessmentButtons
            onDocumentation={onViewDataset}
            onCopyScore={onCopyScore}
            isCopyDisabled={scoreState.status !== 'ready'}
          />
        ) : (
          onViewDataset && <ViewDatasetButton onClick={onViewDataset} />
        )}
      </Stack>
    </Box>
  );
};

export default SupplyChainCard;
